
// Template prompts for each step in the union corporate campaign workflow
export const promptTemplates = {
  corporateContext: `
Objective:

Generate a comprehensive corporate analysis of the {campaign_status} campaign against {target_company} regarding {labor_issue}, identifying key corporate narratives, power structures, and decision-making processes.

Prompt:

You are a senior labor organizer and corporate researcher specializing in {target_company} and corporate campaign strategy. Perform a detailed analysis of the corporate context surrounding the {campaign_name} campaign.

Tasks (Provide detailed and structured responses):
1. Corporate Decision-Making Structure:
Analyze the corporate hierarchy, key decision-makers, and power dynamics within {target_company}. Identify who ultimately makes decisions about {labor_issue}.

2. Corporate Messaging and Public Relations:
Examine {target_company}'s public statements, press releases, and corporate communications regarding {labor_issue}. Identify the narratives they use to justify their position.

3. Anti-Union Strategy Assessment:
Based on {anti_union_tactics}, analyze the company's approach to labor relations and union organizing efforts.

4. Corporate Stakeholder Mapping:
Map the relationships between {corporate_executives}, board members, major shareholders, and external consultants or law firms involved.

5. Corporate Vulnerabilities:
Identify potential pressure points including reputation risks, regulatory concerns, customer relationships, and investor sensitivities.

6. Areas for Strategic Leverage:
Highlight opportunities for campaign pressure including public relations vulnerabilities, regulatory oversight, and stakeholder influence points.
`,

  corporateContextRefinement: `
Objective:

Perform an in-depth analysis of {target_company}'s corporate structure, financial dependencies, and strategic vulnerabilities using the output from Stage 1.

Prompt:

You are now tasked to refine and deepen the corporate analysis based on the detailed report from Stage 1 (provided below). Focus on identifying concrete leverage points and campaign opportunities.

Stage 1 Analysis (for context):
{previous_analysis}

Refinement Tasks (Provide thorough and detailed responses):
1. Financial Leverage Analysis:
Examine {target_company}'s financial dependencies, major contracts, investor relations, and revenue streams that could be pressure points.

2. Corporate Governance Deep-Dive:
Analyze board composition, shareholder structure, executive compensation tied to performance metrics, and governance policies.

3. Stakeholder Influence Mapping:
Map the influence networks including institutional investors, major customers, regulatory bodies, and community relationships.

4. Historical Labor Relations Patterns:
Research {target_company}'s history with unions, previous campaigns, settlements, and patterns of response to worker organizing.

5. Strategic Campaign Opportunities:
Based on the analysis, identify 3-5 specific leverage points where campaign pressure could be most effective.
`,

  economicContext: `
Objective:

Provide a thorough economic analysis of {target_company}'s business model, financial position, and economic relationships relevant to the {campaign_name} campaign.

Prompt:

You are a labor economist and corporate financial analyst. Analyze the economic context of {target_company} as it relates to {labor_issue}.

Tasks (Detailed and structured responses required):
1. Company Financial Analysis:
Examine {target_company}'s revenue, profitability, market position, and financial health. How does {labor_issue} impact their bottom line?

2. Labor Cost Analysis:
Assess the company's labor costs, workforce composition, and how addressing {labor_issue} would affect their financial position.

3. Economic Dependencies:
Identify key economic relationships including major customers, suppliers, investors, and business partners who could influence company decisions.

4. Market Competition Impact:
Analyze how competitors handle similar labor issues and whether {target_company}'s position creates competitive advantages or disadvantages.

5. Economic Pressure Points:
Identify economic vulnerabilities including revenue dependencies, cost structures, and financial obligations that campaign pressure could target.

6. Campaign Economic Strategy:
Develop economic arguments and pressure tactics that could motivate {target_company} to address {labor_issue}.
`,

  workplaceContext: `
Objective:

Provide a comprehensive analysis of workplace conditions, worker experiences, and labor relations at {target_company} regarding {labor_issue}.

Prompt:

You are a workplace conditions analyst and labor rights advocate. Evaluate the workplace dimensions of {labor_issue} at {target_company}.

Tasks (Detailed and structured responses required):
1. Workplace Conditions Assessment:
Document the specific workplace issues related to {labor_issue}, including safety concerns, working conditions, compensation, and worker treatment.

2. Worker Voice and Organizing:
Analyze existing worker organizing efforts, union presence, and employee representation structures at {target_company}.

3. Company Labor Practices:
Examine {target_company}'s policies, procedures, and practices related to {labor_issue} and worker rights.

4. Legal and Regulatory Framework:
Assess relevant labor laws, regulations, and compliance issues related to {labor_issue}.

5. Worker Impact Documentation:
Quantify the impact of current conditions on workers including economic effects, health and safety consequences, and quality of life issues.

6. Solidarity and Support Analysis:
Evaluate potential for worker solidarity, community support, and broader labor movement backing for addressing {labor_issue}.
`,

  stakeholderCommunity: `
Objective:

Perform an in-depth analysis of stakeholders and community dynamics relating to the {campaign_name} campaign against {target_company}.

Prompt:

You are a stakeholder engagement strategist specializing in corporate campaigns and community organizing. Provide a comprehensive analysis of stakeholder positions around {labor_issue}.

Tasks (Detailed and structured responses required):
1. Internal Stakeholder Mapping:
Analyze positions of:
• {corporate_executives} and management
• Workers and {union_leaders}
• Middle management and supervisors
• Board members and major shareholders

2. External Stakeholder Analysis:
Map the positions and influence of:
• {community_supporters} and local organizations
• Customer base and consumer groups
• Regulatory bodies and government officials
• Media outlets and opinion leaders

3. Coalition Building Opportunities:
Identify potential allies including other unions, community organizations, advocacy groups, and political allies.

4. Opposition Analysis:
Assess corporate allies, anti-union organizations, and other opposition forces that may support {target_company}.

5. Community Impact Assessment:
Evaluate how {labor_issue} affects the broader community and potential for community support.

6. Stakeholder Engagement Strategy:
Recommend approaches for building support, neutralizing opposition, and maximizing stakeholder pressure on {target_company}.
`,

  messageFrameAnalysis: `
Objective:
Perform a deep analysis of the language, values, and frames used in discourse around {labor_issue} at {target_company}.

Prompt:
You are a strategic communications expert specializing in labor organizing and corporate campaigns. Analyze the dominant narratives and messaging around {labor_issue}.

Campaign Context:
- Issue: {labor_issue}
- Target: {target_company}
- Campaign: {campaign_name}
- Desired outcome: {primary_goal}
- Primary audience: {target_audience}
- Known workplace dynamics: {workplace_dynamics}

Analysis Tasks:

1. Corporate Messaging Frames
Identify 4–6 common frames {target_company} uses to defend their position on {labor_issue}:
- Economic necessity arguments
- Legal compliance claims
- Worker benefit assertions
- Industry standard justifications
- Analyze how these frames appear in corporate communications

2. Worker and Union Counter-Frames
Map frames used by workers and union supporters:
- Worker dignity and rights
- Economic justice arguments
- Safety and health concerns
- Community impact messaging

3. Values Mapping
Identify the dominant values evoked by different stakeholders:
- Corporate values: efficiency, competitiveness, shareholder value
- Worker values: fairness, dignity, security, solidarity
- Community values: local economic health, social responsibility

4. Narrative Devices and Language Patterns
Examine rhetorical strategies used by both sides:
- Corporate euphemisms and technical language
- Worker solidarity and justice language
- Economic vs. moral framing patterns
- Power relationship descriptions

5. Comparative Campaign Analysis
Reference 2–3 similar corporate campaigns:
- Similar labor issues in comparable companies
- Successful messaging strategies
- Failed approaches and lessons learned

6. Counter-Narrative Preparation
Anticipate corporate responses to campaign messaging:
- Economic scare tactics
- Legal threat messaging
- Divide-and-conquer strategies
- Public relations countermeasures
`,

  messageDevelopment: `
Objective:
Using the frame analysis, develop targeted campaign messages for achieving {primary_goal} regarding {labor_issue} at {target_company}.

Prompt:
You are a strategic communications expert and campaign messaging specialist. Create compelling messages for the corporate campaign.

Campaign Context:
- Issue: {labor_issue}
- Target: {target_company}
- Campaign: {campaign_name}
- Desired outcome: {primary_goal}
- Primary audience: {target_audience}

Message Development Tasks:

1. Four 150-word Pro-Campaign Messages

A. Worker Dignity Message
- Focus on worker rights and human dignity
- Address core concerns of {target_audience}
- Emphasize fairness and respect in the workplace

B. Economic Justice Message
- Highlight economic inequality and corporate responsibility
- Connect to community economic health
- Present clear economic benefits of addressing {labor_issue}

C. Accountability and Transparency Message
- Focus on corporate accountability and responsibility
- Highlight {target_company}'s obligations to workers and community
- Emphasize transparency in corporate decision-making

D. Solidarity and Community Message
- Build on community support and solidarity
- Connect workplace issues to broader community values
- Emphasize collective action and mutual support

2. Corporate Opposition Message Simulation
Create one persuasive corporate counter-message that:
- Reflects {target_company}'s likely arguments
- Uses identified corporate frames
- Incorporates {corporate_motivations} and economic justifications

3. Message Matrix
For each message provide:
- Title and core frame
- Key values emphasized
- Suggested delivery format and channels
- Effectiveness score (0-100) with detailed rationale
- Risk assessment and mitigation strategies
- Specific improvement recommendations

4. Implementation Strategy
- Message sequencing and timing recommendations
- Channel and format suggestions for maximum impact
- Messenger selection guidelines
- Potential risks and response strategies
`,

  campaignPlan: `
Objective:

Translate the messaging strategy into a comprehensive corporate campaign plan aligned to achieve {primary_goal} regarding {labor_issue} at {target_company}.

Prompt:

You are a senior campaign strategist specializing in corporate campaigns and labor organizing. Develop a strategic plan to achieve the campaign objective.

Campaign Plan Tasks:
1. Strategic Campaign Goals:
Define 2–3 SMART objectives that will create pressure on {target_company} to address {labor_issue}.

2. Pressure Campaign Strategy:
Outline a multi-pressure approach including:
• Worker organizing and mobilization
• Community and stakeholder engagement
• Media and public relations strategy
• Economic pressure tactics

3. Target Audience Segmentation:
Profile and prioritize target audiences:
• {target_audience} (primary)
• Secondary decision influencers
• Community supporters and allies
• Media and opinion leaders

4. Campaign Tactics and Timeline:
Suggest specific campaign actions including:
• Worker organizing activities
• Community mobilization events
• Stakeholder pressure campaigns
• Media and communications strategy
• Escalation tactics if needed

5. Opposition Response Planning:
Anticipate {target_company}'s likely responses and counter-strategies:
• Corporate messaging and PR responses
• Legal challenges or intimidation tactics
• Divide-and-conquer attempts
• Economic pressure or retaliation

6. Resource and Capacity Analysis:
Provide framework for:
• Staffing and volunteer coordination
• Budget allocation across tactics
• Timeline and milestone planning
• Success metrics and evaluation criteria
`,

  consolidatedReport: `
# {campaign_name} - Corporate Campaign Analysis Report
Date: ${new Date().toLocaleDateString()}

## Executive Summary

Campaign Overview:
- Labor Issue: {labor_issue}
- Target Company: {target_company}
- Primary Goal: {primary_goal}
- Target Audience: {target_audience}

## Table of Contents

1. Executive Summary
2. Campaign Context
3. Methodology
4. Detailed Analysis Reports
5. Strategic Synthesis & Recommendations
6. Implementation Plan

## Campaign Context

Issue Description:
{issue_description}

Campaign Status: {campaign_status}
Campaign Type: {decision_type}

## Methodology

This analysis was conducted through a series of specialized assessments covering:
- Corporate context and power structure analysis
- Economic and financial leverage points
- Workplace conditions and worker organizing potential
- Stakeholder mapping and community dynamics
- Message framing and strategic communications
- Comprehensive campaign planning

## Detailed Analysis Reports

### 1. Corporate Context Analysis
{step_1_result}

### 2. Corporate Context Refinement
{step_2_result}

### 3. Economic Context Analysis
{step_3_result}

### 4. Workplace Context Analysis
{step_4_result}

### 5. Stakeholder & Community Analysis
{step_5_result}

### 6. Message Frame Analysis
{step_6_result}

### 7. Message Development
{step_7_result}

### 8. Strategic Campaign Plan
{step_8_result}

## Strategic Synthesis & Recommendations

[Generated synthesis based on all analyses]

## Implementation Plan

[Strategic recommendations and action items]
`,
};

// This function replaces placeholders in templates with actual data
export function populateTemplate(template: string, data: any): string {
  let populatedTemplate = template;
  
  // Handle step results for consolidated report
  if (template === promptTemplates.consolidatedReport && data.steps) {
    data.steps.forEach((step: any, index: number) => {
      populatedTemplate = populatedTemplate.replace(
        `{step_${index + 1}_result}`,
        step.result || '[Analysis pending]'
      );
    });
  }

  // Core issue details
  if (data.core_issue_details) {
    populatedTemplate = populatedTemplate
      .replace(/{campaign_name}/g, data.core_issue_details.campaign_name)
      .replace(/{decision_type}/g, data.core_issue_details.decision_type)
      .replace(/{campaign_status}/g, data.core_issue_details.campaign_status)
      .replace(/{campaign_start_date}/g, data.core_issue_details.campaign_start_date || 'Not specified')
      .replace(/{target_company}/g, data.core_issue_details.target_company)
      .replace(/{labor_issue}/g, data.core_issue_details.labor_issue)
      .replace(/{issue_description}/g, data.core_issue_details.issue_description);
  }
  
  // Stakeholders
  if (data.stakeholders) {
    populatedTemplate = populatedTemplate
      .replace(/{corporate_executives}/g, data.stakeholders.corporate_executives.join(', '))
      .replace(/{company_representatives}/g, data.stakeholders.company_representatives.join(', '))
      .replace(/{union_leaders}/g, data.stakeholders.union_leaders.join(', '))
      .replace(/{worker_allies}/g, data.stakeholders.worker_allies.join(', '))
      .replace(/{community_supporters}/g, data.stakeholders.community_supporters.join(', '))
      .replace(/{corporate_interests}/g, data.stakeholders.corporate_interests.join(', '))
      .replace(/{workplace_dynamics}/g, data.stakeholders.workplace_dynamics);
  }
  
  // Corporate & Strategic
  if (data.corporate_and_strategic) {
    populatedTemplate = populatedTemplate
      .replace(/{corporate_motivations}/g, data.corporate_and_strategic.corporate_motivations)
      .replace(/{anti_union_tactics}/g, data.corporate_and_strategic.anti_union_tactics)
      .replace(/{corporate_vulnerabilities}/g, data.corporate_and_strategic.corporate_vulnerabilities);
  }
  
  // Campaign Outcome
  if (data.campaign_outcome) {
    populatedTemplate = populatedTemplate
      .replace(/{primary_goal}/g, data.campaign_outcome.primary_goal)
      .replace(/{target_audience}/g, data.campaign_outcome.target_audience);
  }
  
  return populatedTemplate;
}
